import { dataBR, dataCurta, fmtContratos } from "./cotDados";

/**
 * Gráficos e tabela da Posição dos Fundos (COT).
 *
 * As opções do Highcharts são montadas só com dados (sem funções), para o
 * mesmo objeto servir à tela e ao código incorporado nas matérias: o que a
 * redação vê é exatamente o que vai ao ar.
 */

export const FONTE_COT = "RURAL BUSINESS/CFTC";

export const COT_VISOES = [
  { key: "liquida", label: "Posição líquida dos fundos" },
  { key: "compradosVendidos", label: "Fundos: comprados x vendidos" },
  { key: "variacao", label: "Variação semanal do líquido" },
  { key: "indice", label: "COT Index (3 anos)" },
  { key: "participantes", label: "Fundos x produtores x swap dealers" },
  { key: "contratos", label: "Contratos em aberto" },
];

const VERDE = "#3DAA73";
const VERMELHO = "#E34B4B";
const AZUL = "#3F8FE0";
const LARANJA = "#F0A32E";

const mil = (v) => (v === null || v === undefined ? null : Math.round(v / 100) / 10);

function rotuloFinal(dados, decimais = 1) {
  // Só o último ponto ganha rótulo: é o número que a matéria cita.
  return dados.map((y, i) =>
    i === dados.length - 1 && y !== null
      ? {
          y,
          dataLabels: {
            enabled: true,
            format: `{point.y:,.${decimais}f}`,
            style: { color: "#ffffff", textOutline: "1px #000000", fontSize: "12px", fontWeight: "700" },
          },
        }
      : y
  );
}

function base({ titulo, subtitulo, categorias, unidade, legenda }) {
  return {
    chart: {
      backgroundColor: "#0d0d0d",
      style: { fontFamily: "Arial, sans-serif" },
      // Espaço embaixo para a fonte não encostar nas datas inclinadas.
      spacing: [12, 14, 24, 10],
      height: 380,
    },
    title: {
      text: titulo,
      style: { color: "#F0F000", fontSize: "16px", fontWeight: "700" },
    },
    subtitle: {
      text: `${subtitulo}${unidade ? ` (${unidade})` : ""}`,
      style: { color: "#4EA6FF", fontSize: "12px", fontWeight: "600" },
    },
    credits: {
      enabled: true,
      text: `Fonte: ${FONTE_COT}`,
      href: "",
      position: { align: "right", x: -10, y: -8 },
      style: { color: "#ffffff", fontSize: "10px", cursor: "default" },
    },
    exporting: { enabled: false },
    legend: {
      enabled: legenda,
      itemStyle: { color: "#e0e0e0", fontWeight: "600", fontSize: "11px" },
      itemHoverStyle: { color: "#ffffff" },
    },
    xAxis: {
      categories: categorias,
      lineColor: "#444444",
      tickColor: "#444444",
      labels: { style: { color: "#cfcfcf", fontSize: "10px" } },
    },
    yAxis: {
      title: { text: null },
      gridLineColor: "#2a2a2a",
      labels: { style: { color: "#cfcfcf", fontSize: "10px" }, format: "{value:,.0f}" },
      plotLines: [{ value: 0, color: "#777777", width: 1, zIndex: 3 }],
    },
    tooltip: { shared: true, valueDecimals: 1, valueSuffix: unidade === "mil contratos" ? " mil" : "" },
    plotOptions: {
      series: { animation: false, marker: { enabled: false } },
      column: { borderWidth: 0, groupPadding: 0.08, pointPadding: 0.04 },
    },
  };
}

/**
 * Opções de uma visão. `linhas` já vem cortado no período exibido.
 */
export function opcoesCot(visao, linhas, { mercado, cor = VERDE }) {
  const categorias = linhas.map((l) => dataCurta(l.data));
  const tituloMercado = `${mercado.label} - ${mercado.bolsa}`;
  const comum = { categorias, legenda: false, unidade: "mil contratos" };

  switch (visao) {
    case "liquida":
      return {
        ...base({ ...comum, titulo: tituloMercado, subtitulo: "Posição líquida dos fundos" }),
        series: [
          {
            type: "column",
            name: "Líquido dos fundos",
            color: cor,
            negativeColor: VERMELHO,
            data: rotuloFinal(linhas.map((l) => mil(l.liquido))),
          },
        ],
      };
    case "compradosVendidos":
      return {
        ...base({ ...comum, legenda: true, titulo: tituloMercado, subtitulo: "Fundos comprados x vendidos" }),
        series: [
          { type: "line", name: "Comprados", color: cor, lineWidth: 2.5, data: rotuloFinal(linhas.map((l) => mil(l.mmLong))) },
          { type: "line", name: "Vendidos", color: VERMELHO, lineWidth: 2.5, data: rotuloFinal(linhas.map((l) => mil(l.mmShort))) },
        ],
      };
    case "variacao":
      return {
        ...base({ ...comum, titulo: tituloMercado, subtitulo: "Variação semanal da posição líquida dos fundos" }),
        series: [
          {
            type: "column",
            name: "Variação na semana",
            color: cor,
            negativeColor: VERMELHO,
            data: rotuloFinal(linhas.map((l) => mil(l.liquidoVar))),
          },
        ],
      };
    case "indice": {
      const opcoes = base({ ...comum, unidade: "0 a 100", titulo: tituloMercado, subtitulo: "COT Index dos fundos - janela de 3 anos" });
      return {
        ...opcoes,
        yAxis: {
          ...opcoes.yAxis,
          min: 0,
          max: 100,
          tickInterval: 10,
          plotLines: [],
          plotBands: [
            {
              from: 90,
              to: 100,
              color: "rgba(61,170,115,0.20)",
              label: { text: "Extremo comprado", style: { color: "#9ad9b6", fontSize: "10px" }, y: 12 },
            },
            {
              from: 0,
              to: 10,
              color: "rgba(227,75,75,0.20)",
              label: { text: "Extremo vendido", style: { color: "#f0a0a0", fontSize: "10px" }, y: 12 },
            },
          ],
        },
        tooltip: { shared: true, valueDecimals: 0 },
        series: [
          {
            type: "line",
            name: "COT Index",
            color: cor === VERDE ? "#F0F000" : cor,
            lineWidth: 2.5,
            data: rotuloFinal(linhas.map((l) => (l.indice === null ? null : Math.round(l.indice))), 0),
          },
        ],
      };
    }
    case "participantes":
      return {
        ...base({ ...comum, legenda: true, titulo: tituloMercado, subtitulo: "Posição líquida por participante" }),
        series: [
          { type: "line", name: "Fundos (Managed Money)", color: cor, lineWidth: 2.5, data: rotuloFinal(linhas.map((l) => mil(l.liquido))) },
          { type: "line", name: "Produtores/comerciais", color: AZUL, lineWidth: 2, data: rotuloFinal(linhas.map((l) => mil(l.prodLiquido))) },
          { type: "line", name: "Swap dealers", color: LARANJA, lineWidth: 2, data: rotuloFinal(linhas.map((l) => mil(l.swapLiquido))) },
        ],
      };
    case "contratos":
      return {
        ...base({ ...comum, titulo: tituloMercado, subtitulo: "Contratos em aberto" }),
        yAxis: { ...base(comum).yAxis, plotLines: [] },
        series: [
          {
            type: "area",
            name: "Contratos em aberto",
            color: cor === VERDE ? AZUL : cor,
            fillOpacity: 0.35,
            lineWidth: 2,
            data: rotuloFinal(linhas.map((l) => mil(l.oi))),
          },
        ],
      };
    default:
      return null;
  }
}

const escaparJson = (obj) =>
  JSON.stringify(obj)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(new RegExp(String.fromCharCode(0x2028), "g"), "\\u2028")
    .replace(new RegExp(String.fromCharCode(0x2029), "g"), "\\u2029");

/** Código para incorporar um gráfico na matéria (iframe, como Comex/USDA). */
export function iframeDoGrafico(opcoes) {
  const altura = opcoes.chart?.height || 380;
  const documento = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <style>* { margin: 0; padding: 0; box-sizing: border-box; } html, body { background:#0d0d0d; overflow:hidden; }</style>
</head>
<body>
  <div id="rb-cot-chart" style="width:100%;height:${altura}px;"></div>
  <script id="rb-cot-data" type="application/json">${escaparJson({ ...opcoes, chart: { ...opcoes.chart, height: null } })}</script>
  <script src="https://web.ruralbusiness.com.br/highcharts.js"></script>
  <script>
    (function () {
      var no = document.getElementById('rb-cot-data');
      if (!no || typeof Highcharts === 'undefined') return;
      Highcharts.setOptions({ lang: { decimalPoint: ',', thousandsSep: '.', numericSymbols: null } });
      Highcharts.chart('rb-cot-chart', JSON.parse(no.textContent || '{}'));
    }());
  </script>
</body>
</html>`;
  return `<iframe src="data:text/html;charset=utf-8,${encodeURIComponent(documento)}" width="100%" height="${altura}" frameborder="0" scrolling="no" style="border:none;display:block;width:100%;"></iframe>`;
}

const MODELO_GERADOR = {
  liquida: "coluna-agrupada",
  variacao: "coluna-agrupada",
  compradosVendidos: "linha-historica",
  participantes: "linha-historica",
  indice: "linha-historica",
  contratos: "area-evolucao",
};

/** Spec do Gerador de imagens (mesmo formato usado por Comex e USDA). */
export function specCotParaGerador(visao, opcoes) {
  const series = (opcoes.series || []).map((s) => ({
    nome: s.name,
    cor: s.color,
    valores: (s.data || []).map((p) => (p && typeof p === "object" ? p.y : p)),
  }));
  const decimais = visao === "indice" ? 0 : 1;
  const unidade = visao === "indice" ? "0 a 100" : "mil contratos";
  return {
    modelo: MODELO_GERADOR[visao] || "linha-historica",
    titulo: [
      { texto: `${opcoes.title.text} `, cor: "#F0F000" },
      { texto: `(${unidade})`, cor: "#FFFFFF", tamanho: 9 },
    ],
    subtitulo: [{ texto: opcoes.subtitle.text.replace(/ \([^)]*\)$/, ""), cor: "#4EA6FF" }],
    fonte: `Fonte: ${FONTE_COT}`,
    fundo: "#000000",
    fundoPlot: "nenhum",
    dados: { categorias: opcoes.xAxis.categories.map(String), series },
    legenda: { visivel: series.length > 1 },
    // Só o último valor rotulado, como na tela (lista de índices do gerador).
    valores: { decimais, prefixo: "", sufixo: "", exibirEm: [opcoes.xAxis.categories.length - 1] },
    rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
    extras: MODELO_GERADOR[visao] === "area-evolucao" ? { suave: true, opacidadeArea: 0.55 } : {},
  };
}

// ---------------------------------------------------------------- tabela

export const COLUNAS_TABELA = [
  { key: "data", titulo: "Semana (terça)", fmt: (l) => dataBR(l.data) },
  { key: "mmLong", titulo: "Comprados", fmt: (l) => fmtContratos(l.mmLong) },
  { key: "mmShort", titulo: "Vendidos", fmt: (l) => fmtContratos(l.mmShort) },
  { key: "liquido", titulo: "Líquido", fmt: (l) => fmtContratos(l.liquido) },
  { key: "liquidoVar", titulo: "Var. semana", fmt: (l) => fmtContratos(l.liquidoVar, true) },
  { key: "mmLongPct", titulo: "% OI comprados", fmt: (l) => `${l.mmLongPct.toLocaleString("pt-BR", { minimumFractionDigits: 1 })}%` },
  { key: "mmShortPct", titulo: "% OI vendidos", fmt: (l) => `${l.mmShortPct.toLocaleString("pt-BR", { minimumFractionDigits: 1 })}%` },
  { key: "oi", titulo: "Contratos em aberto", fmt: (l) => fmtContratos(l.oi) },
  { key: "indice", titulo: "COT Index", fmt: (l) => (l.indice === null ? "—" : String(Math.round(l.indice))) },
];

/** Tabela HTML com estilo próprio, para colar na matéria. */
export function htmlDaTabela(linhas, { mercado, relatorio }) {
  const th = COLUNAS_TABELA.map(
    (c) => `<th style="padding:6px 8px;background:#1a1a1a;color:#F0F000;font-size:12px;text-align:${c.key === "data" ? "left" : "right"};white-space:nowrap;">${c.titulo}</th>`
  ).join("");
  const trs = linhas
    .map((l, i) => {
      const tds = COLUNAS_TABELA.map((c) => {
        const negativo = (c.key === "liquido" || c.key === "liquidoVar") && l[c.key] < 0;
        const positivo = c.key === "liquidoVar" && l[c.key] > 0;
        const cor = negativo ? "#c0392b" : positivo ? "#1e8449" : "#222222";
        return `<td style="padding:5px 8px;border-bottom:1px solid #e5e5e5;font-size:12px;color:${cor};text-align:${c.key === "data" ? "left" : "right"};white-space:nowrap;">${c.fmt(l)}</td>`;
      }).join("");
      return `<tr style="background:${i % 2 ? "#f7f7f7" : "#ffffff"};">${tds}</tr>`;
    })
    .join("");
  return `<div style="overflow-x:auto;font-family:Arial,sans-serif;">
<p style="margin:0 0 4px;font-size:14px;font-weight:700;">Posição dos fundos (Managed Money) - ${mercado.label} (${mercado.bolsa})</p>
<table style="border-collapse:collapse;width:100%;min-width:640px;"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>
<p style="margin:4px 0 0;font-size:10px;color:#666666;">Em contratos. ${relatorio}. Fonte: ${FONTE_COT} (Commitments of Traders, disaggregated).</p>
</div>`;
}

/** Mesma tabela separada por tabulação, para colar no Excel. */
export function tsvDaTabela(linhas) {
  const cab = COLUNAS_TABELA.map((c) => c.titulo).join("\t");
  return [cab, ...linhas.map((l) => COLUNAS_TABELA.map((c) => c.fmt(l)).join("\t"))].join("\n");
}
